import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminOrdersComponent } from './admin-orders.component';
import { StaffOrdersComponent } from './staff-orders.component';
import { RoleService } from '../services/role.service';
import { Subject } from 'rxjs';
import { destroyRoleWrapper, initializeRoleWrapper } from '../services/role-wrapper.utils';

/**
 * Wrapper component that selects and displays either the admin orders view or the staff orders view based on the current user's role.
 * @component
 * @selector app-orders-wrapper
 * @example
 *   <app-orders-wrapper></app-orders-wrapper>
 * @input boolean isAdminUser - Internal flag indicating whether the current user has an admin role and therefore whether the AdminOrders component should be shown. This is not an @Input() bound from the parent; it is derived from RoleService at runtime.
 * @output none none - This wrapper does not emit events itself; any user interactions or events are emitted by the child components (app-admin-orders or app-staff-orders).
 *
 * Purpose:
 *   - Acts as a thin presentation/controller wrapper that chooses between app-admin-orders and app-staff-orders depending on the authenticated user's role.
 *   - Keeps role-resolution logic centralized so child components can focus on their specific UI and behavior.
 *
 * Key methods and collaborators:
 *   - constructor(roleService: RoleService): injects RoleService used to determine the current user's role.
 *   - initializeRoleWrapper(thisComponent, roleService, destroy$): invoked in ngOnInit to set up role detection and to update isAdminUser accordingly (delegated helper).
 *   - destroyRoleWrapper(destroy$): invoked in ngOnDestroy to clean up subscriptions (delegated helper).
 *
 * Lifecycle hooks:
 *   - ngOnInit(): Initializes the role-based view selection by calling initializeRoleWrapper and subscribing as needed.
 *   - ngOnDestroy(): Ensures proper teardown of subscriptions via destroyRoleWrapper and the destroy$ Subject.
 *
 * Notes:
 *   - The component is standalone and composes AdminOrdersComponent and StaffOrdersComponent.
 *   - It exposes no public inputs or outputs for parent components; it encapsulates role-based rendering internally.
 */
@Component({
  selector: 'app-orders-wrapper',
  standalone: true,
  imports: [CommonModule, AdminOrdersComponent, StaffOrdersComponent],
  template: `
    <!-- Admin Orders Component -->
    <app-admin-orders *ngIf="isAdminUser"></app-admin-orders>

    <!-- Staff Orders Component -->
    <app-staff-orders *ngIf="!isAdminUser"></app-staff-orders>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class OrdersWrapperComponent implements OnInit, OnDestroy {
  isAdminUser = false;
  private destroy$ = new Subject<void>();

  constructor(private roleService: RoleService) {}

  ngOnInit(): void {
    initializeRoleWrapper(this, this.roleService, this.destroy$);
  }

  ngOnDestroy(): void {
    destroyRoleWrapper(this.destroy$);
  }
}
